import { apiFetch } from "@core/http";
import type { components } from "@core/types/openapi";

export type Vehicle = components["schemas"]["Vehicle"];
export type VehicleInput = components["schemas"]["VehicleInput"];
export type Maintenance = components["schemas"]["Maintenance"];
export type MaintenanceInput = components["schemas"]["MaintenanceInput"];
export type CatalogEntry = components["schemas"]["CatalogEntry"];

// SPEC-203: los catálogos de vehículo y de mantenimiento comparten el mismo
// recurso genérico de catálogos que ya usa members/api.ts. `inactivation-reasons`
// es el catálogo de motivos de baja: el mismo que usa la inactivación de
// miembro (SPEC-202), no uno propio de vehículos.
export type VehicleCatalog = "car-models" | "colors" | "inactivation-reasons";
export type MaintenanceCatalog = "maintenance-types" | "workshops";

const vehiclesBase = (memberId: string) =>
  `/v1/portal/members/${encodeURIComponent(memberId)}/vehicles`;
const vehicleBase = (id: string) =>
  `/v1/portal/vehicles/${encodeURIComponent(id)}`;
const maintenancesBase = (vehicleId: string) =>
  `${vehicleBase(vehicleId)}/maintenances`;

export const listVehicles = (memberId: string, includeDeactivated = false) =>
  apiFetch<Vehicle[]>(
    `${vehiclesBase(memberId)}${includeDeactivated ? "?includeDeactivated=true" : ""}`,
  );
export const createVehicle = (memberId: string, input: VehicleInput) =>
  apiFetch<Vehicle>(vehiclesBase(memberId), { method: "POST", body: input });
export const editVehicle = (id: string, input: VehicleInput) =>
  apiFetch<Vehicle>(vehicleBase(id), { method: "PUT", body: input });
// 204 sin cuerpo (SPEC-203 §3): la baja no inactiva la membresía por sí sola,
// eso es un acto aparte de SPEC-202.
export const deactivateVehicle = (
  id: string,
  input: { deactivationReasonId: string; confirmed: boolean },
) => apiFetch<void>(vehicleBase(id), { method: "DELETE", body: input });

export const listMaintenances = (vehicleId: string) =>
  apiFetch<Maintenance[]>(maintenancesBase(vehicleId));
export const createMaintenance = (vehicleId: string, input: MaintenanceInput) =>
  apiFetch<Maintenance>(maintenancesBase(vehicleId), {
    method: "POST",
    body: input,
  });

export const listVehicleCatalog = (
  catalog: VehicleCatalog | MaintenanceCatalog,
) => apiFetch<CatalogEntry[]>(`/v1/portal/catalogs/${catalog}/entries`);

// `CatalogEntry.name` es bilingüe solo en los catálogos que el miembro ve
// (ADR-007); los de esta pantalla son de uso interno del portal, así que
// llega como string o como { es, en } según el catálogo (contrato).
export const catalogLabel = (entry: CatalogEntry) =>
  typeof entry.name === "string" ? entry.name : entry.name.es;

// `Vehicle`/`Maintenance` (modelos de lectura) exponen solo el valor de
// presentación (`model`, `color`, `workshop`) — nunca el id que exige
// `VehicleInput`/`MaintenanceInput` para editar (contrato, sin cambios
// posibles acá). Para no perder la selección al editar sin tocar el
// selector, se resuelve el id cruzando esa etiqueta contra el catálogo ya
// cargado. Si hay más de una entrada con la misma etiqueta, la coincidencia
// es ambigua a propósito: se prefiere dejar el campo sin resolver (fuerza a
// la persona a re-seleccionar) antes que adivinar una de varias.
export const resolveCatalogId = (
  entries: readonly CatalogEntry[],
  label?: string | null,
): string => {
  if (!label) return "";
  const matches = entries.filter((entry) => catalogLabel(entry) === label);
  return matches.length === 1 ? (matches[0]?.id ?? "") : "";
};
